import { Button, Col, Divider, Popconfirm, Row, Space, Table } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import moment from 'moment';
import {
  DownloadOutlined,
  EyeOutlined,
  PaperClipOutlined,
} from '@ant-design/icons';
import Title from 'antd/lib/typography/Title';
import styled from 'styled-components';

import { priceToString, timeForToday } from '../../util/utils';
import { styledTable } from '../../components/styled/mixin';

const StyledDiv = styled.div`
  ${styledTable}
`;

interface RevenueIncomeListComponentProps {
  incomeList: any[];
  openCreateIncomeModal: () => void;
  openUpdateIncomeModal: (income: any) => void;
  onDeleteIncome: (incomeId: number) => void;
  downloadFile: (attachFileId: string, fileName: string) => void;
  openImagePreview: (imageData: any) => void;
}

function RevenueIncomeListComponent({
  incomeList,
  openCreateIncomeModal,
  openUpdateIncomeModal,
  onDeleteIncome,
  downloadFile,
  openImagePreview,
}: RevenueIncomeListComponentProps) {
  const columns: ColumnsType<any> = [
    {
      title: '입금일',
      align: 'center',
      dataIndex: 'depositDate',
      width: '13%',
    },
    {
      title: '설명',
      dataIndex: 'description',
      width: '45%',
      render: (text: any, record: any, index: any) => {
        let children: any = [];
        children.push(
          <div key={0} style={{ whiteSpace: 'pre-wrap' }}>
            {record.description}
          </div>,
        );
        if (record.file.length !== 0) {
          children.push(
            <Divider
              key={1}
              type="horizontal"
              style={{ marginTop: '5px', marginBottom: '5px' }}
            />,
          );
        }

        for (const _file of record.file) {
          children.push(
            <Row justify={'space-between'} key={_file.id}>
              <Space>
                <PaperClipOutlined rev={undefined} />
                <span>{_file.name}</span>
              </Space>
              <Space>
                <Button
                  size="small"
                  icon={<DownloadOutlined rev={undefined} />}
                  onClick={() => downloadFile(_file.id, _file.name)}
                >
                  {/* 다운로드 */}
                </Button>
                <Button
                  size="small"
                  icon={<EyeOutlined rev={undefined} />}
                  disabled={
                    _file.name.split('.').pop() !== 'jpg' &&
                    _file.name.split('.').pop() !== 'jpeg' &&
                    _file.name.split('.').pop() !== 'png' &&
                    _file.name.split('.').pop() !== 'pdf'
                  }
                  onClick={() => {
                    openImagePreview(_file);
                  }}
                ></Button>
              </Space>
            </Row>,
          );
        }
        return <>{children}</>;
      },
    },
    {
      title: '담당자',
      width: '10%',
      align: 'center',
      dataIndex: ['user', 'name'],
    },
    {
      title: '금액(원)',
      align: 'right',
      dataIndex: 'amount',
      width: '15%',
      render: (text: any, record: any) => {
        return priceToString(record.amount);
      },
    },
    {
      title: '변경일시',
      dataIndex: 'updatedAt',
      align: 'center',
      sorter: (a: any, b: any) => a.updatedAt.localeCompare(b.updatedAt),
      sortOrder: 'ascend',
      width: '12%',
      render: (text: any, record: any, index: any) =>
        timeForToday(moment(record.updatedAt).format('YYYY-MM-DD HH:mm:ss')),
    },
    {
      title: '',
      key: 'action',
      render: (text, record) => (
        <Space size="small">
          <Button size="small" onClick={() => openUpdateIncomeModal(record)}>
            수정
          </Button>
          <div onClick={(e) => e.stopPropagation()}>
            <Popconfirm
              title="정말 삭제하시겠습니까?"
              onConfirm={() => onDeleteIncome(record.id)}
            >
              <Button size="small">삭제</Button>
            </Popconfirm>
          </div>
        </Space>
      ),
    },
  ];

  return (
    <StyledDiv>
      <Row justify={'space-between'} style={{ marginTop: '30px' }}>
        <Col>
          <Title level={5}>수금현황</Title>
        </Col>
        <Col>
          <Button onClick={openCreateIncomeModal} size="small">
            추가
          </Button>
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          <Table<any>
            rowKey="id"
            columns={columns}
            dataSource={incomeList}
            pagination={{
              defaultPageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20'],
              position: ['bottomCenter'],
            }}
            summary={(pageData) => {
              let totalValue = 0;

              pageData.forEach(({ amount }) => {
                totalValue += amount;
              });

              return (
                <>
                  {
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0}></Table.Summary.Cell>
                      <Table.Summary.Cell index={1}></Table.Summary.Cell>
                      <Table.Summary.Cell index={2} align="right">
                        Total:
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={3} align="right">
                        {priceToString(totalValue)}
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={4}></Table.Summary.Cell>
                      <Table.Summary.Cell index={5}></Table.Summary.Cell>
                    </Table.Summary.Row>
                  }
                </>
              );
            }}
          />
        </Col>
      </Row>
    </StyledDiv>
  );
}

export default RevenueIncomeListComponent;
